import { useMyJoinRequests, useTrips, useWithdrawJoinRequest } from '@hooks'
import { myJoinRequestCards, type MyJoinRequestCard } from '@lib/joinRequests'
import { cancelRequestConfirmation } from '@lib/membership'
import ConfirmationModal from '@components/ConfirmationModal'
import type { TripCreateSource } from '@orbit/api-types'
import { Trip } from '@types'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CreateTripLocationState } from './CreateTripFlow/paths'
import JoinTripModal from './JoinTripModal'
import TripsList from './TripsList'

const TripsPage: React.FC = () => {
	const navigate = useNavigate()
	const { data: tripsData, isLoading, error } = useTrips()
	const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
	const { data: requestsData } = useMyJoinRequests()
	const { withdraw, withdrawingCode } = useWithdrawJoinRequest()
	// A pending request is withdrawn only after asking; a denied one is dismissed straight away.
	const [cancelling, setCancelling] = useState<MyJoinRequestCard | null>(null)
	const cancelCopy = cancelling ? cancelRequestConfirmation(cancelling.tripName) : null

	const handleWithdrawRequest = (request: MyJoinRequestCard) => {
		if (request.isPending) setCancelling(request)
		else withdraw(request.code)
	}

	const handleTripClick = (trip: Trip) => {
		navigate(`/trips/${trip.id}`)
	}

	const handleCreateTrip = (source: TripCreateSource) => {
		const state: CreateTripLocationState = { source }
		navigate('/trips/create', { state })
	}

	const handleJoinTrip = () => {
		setIsJoinModalOpen(true)
	}

	const trips = tripsData?.trips || []

	return (
		<>
			<TripsList
				trips={trips}
				loading={isLoading}
				error={error ? 'Failed to load trips' : null}
				onTripClick={handleTripClick}
				onCreateTrip={handleCreateTrip}
				onJoinTrip={handleJoinTrip}
				joinRequests={myJoinRequestCards(requestsData?.requests ?? [], trips)}
				onWithdrawRequest={handleWithdrawRequest}
				withdrawingCode={withdrawingCode}
			/>
			<JoinTripModal isOpen={isJoinModalOpen} onClose={() => setIsJoinModalOpen(false)} />
			<ConfirmationModal
				isOpen={cancelling !== null}
				onClose={() => setCancelling(null)}
				onConfirm={() => cancelling && withdraw(cancelling.code)}
				title={cancelCopy?.title ?? ''}
				message={cancelCopy?.message ?? ''}
				confirmText={cancelCopy?.confirmText}
				cancelText={cancelCopy?.cancelText}
				variant="danger"
			/>
		</>
	)
}

export default TripsPage
